/**
 * Colonnes et lignes d'une table du document, depuis la réponse brute de l'API.
 *
 * Point structurant : **l'API n'a pas de clé technique de colonne, le libellé
 * EST la clé** — une ligne est un objet dont les clés sont les noms de
 * colonnes, et les colonnes se déduisent de `Object.keys(rows[0])`. Les clés
 * `col…` posées ici sont régénérées à chaque lecture ; c'est pourquoi tout ce
 * qui doit survivre à un rechargement (masquage, ordre, largeur, renommage) se
 * retrouve par **libellé d'origine**, jamais par clé.
 *
 * Module partagé document ↔ site : pur, sans React ni store.
 */

import { colId, rowId } from '../../constants/constants';
import { isAnImageUrl } from '../../utils/browser';

const getAlign = ({ columnsInformations, columnIndex }) => {
  if (!columnsInformations || columnIndex >= columnsInformations.length) {
    if (!!columnsInformations && columnsInformations.length > 0) {
      return (
        columnsInformations[columnsInformations.length - 1].next_align ??
        'start'
      );
    }
    return 'start';
  }
  return columnsInformations[columnIndex].align ?? 'start';
};

const getWidth = ({
  columnNames,
  columnsInformations,
  columnIndex,
  boxWidth,
}) => {
  if (!columnsInformations || columnNames.length === 1)
    return boxWidth / columnNames.length;

  if (columnIndex >= columnsInformations.length) {
    const perc =
      (100 - columnsInformations.reduce((acc, { width }) => acc + width, 0)) /
      (columnNames.length - columnsInformations.length);
    return (perc * boxWidth) / 100;
  }

  return (columnsInformations[columnIndex].width * boxWidth) / 100;
};

const extractColumnsInformations = (columns_informations) => {
  const columnsInformations = columns_informations.map((column) => {
    if (column.children) {
      return extractColumnsInformations(column.children).flat();
    }
    return column;
  });
  return columnsInformations.flat();
};

const buildColumnsDisposition = (
  columns_informations,
  flatColumns,
  columnIndex = { value: 0 }
) => {
  return columns_informations.map((column) => {
    if (column.children) {
      // Colonne parent - lui assigner une nouvelle key
      return {
        ...column,
        key: colId(),
        children: buildColumnsDisposition(
          column.children,
          flatColumns,
          columnIndex
        ),
      };
    }
    // Colonne feuille - utiliser la key de la colonne correspondante dans flatColumns
    const correspondingColumn = flatColumns[columnIndex.value];
    columnIndex.value++;
    return {
      ...column,
      key: correspondingColumn?.key ?? colId(),
    };
  });
};

// Types des lignes d'une slice (`row_kinds` de l'API). L'API envoie toujours
// `row_kinds`, mais seulement pour les slices qui ont une zone : une slice
// absente vaut « aucune ligne typée » ([]). undefined = réponse antérieure aux
// types de ligne.
export const rowKindsOf = (table, key) =>
  table.row_kinds ? table.row_kinds[key] ?? [] : undefined;

// `kinds` : types des lignes (cf. rowKindsOf, alignés sur `rows`). Posés en
// `rowKind` sur les lignes une fois les colonnes déduites — dans les lignes de
// l'API, ils deviendraient une colonne. Une ligne ordinaire reçoit
// `rowKind: null` : une box dont les lignes n'ont pas la clé a été lue avant
// les types de ligne, et ses fusions par type attendent un rafraîchissement
// (cf. hasStaleRowKinds).
export const getTableColumnsData = (table, boxWidth) => {
  const { rows, columns_informations, kinds } = table;
  const columnsInformations = columns_informations
    ? extractColumnsInformations(columns_informations)
    : [];
  const columnNames = Object.keys(rows[0]);
  let totalWidth = 0;

  const columns = columnNames.map((columnName, columnIndex) => {
    const nonEmptyCellValues = rows
      .map((row) => row?.[columnName])
      .filter((n) => n || n === 0);
    // Type image explicite via columns_informations (tableaux planning-screenshot) :
    // garde la colonne en image même quand toutes les cellules sont vides (screenshot
    // en cours de régénération) -> Cell affiche le loader. Sinon fallback heuristique
    // sur le contenu (URLs image) pour les colonnes image sans marqueur.
    const isImage =
      columnsInformations[columnIndex]?.type === 'image' ||
      (nonEmptyCellValues.length > 0 && nonEmptyCellValues.every(isAnImageUrl));
    let width = getWidth({
      columnNames,
      columnsInformations,
      columnIndex,
      boxWidth,
    });
    totalWidth += width;
    if (columnIndex === columnNames.length - 1 && totalWidth < boxWidth) {
      width = boxWidth - totalWidth + width;
    }

    const column = {
      title: columnName,
      key: colId(),
      width,
      type: isImage ? 'image' : 'text',
      textAlign: getAlign({ columnsInformations, columnIndex }),
      merge: columnsInformations[columnIndex]?.merge ?? false,
    };
    return column;
  });

  let columnsDisposition = [];
  if (columns_informations) {
    // `columns_informations` can also be a style descriptor (width/align only).
    // Only build hierarchical headers when it maps every leaf column.
    const canBuildDisposition = columnsInformations.length === columns.length;
    if (canBuildDisposition) {
      columnsDisposition = buildColumnsDisposition(
        columns_informations,
        columns
      );
    }
  }

  return {
    columns,
    columnsDisposition,
    data: rows
      .map((row, index) => row && {
        ...row,
        key: rowId(),
        ...(kinds ? { rowKind: kinds[index] ?? null } : {}),
      })
      .filter((row) => row),
  };
};

// Box lue avant les types de ligne : aucune de ses lignes venues de l'API ne
// porte la clé `rowKind`. Les lignes ajoutées à la main (`manual`) n'en disent
// rien.
export const hasStaleRowKinds = (data) => {
  const apiRows = (data ?? []).filter((row) => row && !row.manual);
  return apiRows.length > 0 && !apiRows.some((row) => 'rowKind' in row);
};

/**
 * Catalogue de colonnes `{ label, width, align }` (largeur en %) tiré d'une
 * réponse brute de l'API. Sert aux tableaux historiques, qui n'ont pas de
 * catalogue backend : leurs colonnes sont les clés des lignes renvoyées.
 * `identifiers` désigne la slice d'une box découpée (rows_<ids>) ; à défaut,
 * la première slice non vide.
 */
export const catalogFromRawTable = (table, identifiers = null) => {
  if (!table) return [];
  const rowKeys = Object.keys(table).filter((key) => key.startsWith('rows'));
  const wanted = identifiers?.length ? `rows_${identifiers.join('_')}` : null;
  const key = rowKeys.find((k) => k === wanted && table[k]?.length > 0)
    ?? rowKeys.find((k) => Array.isArray(table[k]) && table[k].length > 0);
  if (!key) return [];
  const { columns } = getTableColumnsData(
    { rows: table[key], columns_informations: table.columns_informations },
    100
  );
  return columns.map((col) => ({
    label: col.title,
    width: Number(col.width.toFixed(2)),
    align: col.textAlign,
    ...(col.type === 'image' ? { type: 'image' } : {}),
  }));
};
